<template>
  <form ref="formElement" @submit.prevent="submit" autocomplete="off">
    <section class="card__body">
      <h3>{{ language.title_business_details }}</h3>
      <div class="row">
        <div class="col-md-6">
          <SaxxText name="companyName" :form="formName" required>{{ language.label_company_name }}</SaxxText>
        </div>
        <div class="col-md-6">
          <SaxxText name="tradeName" :form="formName" required>{{ language.label_trade_name }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxSelect
              name="country"
              :items="countries"
              :form="formName"
              required
          >{{ language.label_country }}
          </SaxxSelect>
        </div>

        <div class="col-md-6">
          <SaxxSelect
              v-if="provinces.length > 0"
              name="province"
              :items="provinces"
              :form="formName"
              required
          >
            <template v-if="fields.country === 'USA'">
              {{ language.label_state }}
            </template>
            <template v-else>{{ language.label_province }}</template>
          </SaxxSelect>
          <SaxxText v-else name="province" :form="formName">{{ language.label_province }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-3">
          <SaxxText name="city" :form="formName" required>{{ language.label_city }}</SaxxText>
        </div>

        <div class="col-md-3">
          <SaxxText name="address" maxlength="35" :form="formName" required>{{
              language.label_address_line_1
            }}
          </SaxxText>
        </div>

        <div class="col-md-3">
          <SaxxText name="address2" maxlength="20" :form="formName">{{ language.label_address_line_2 }}</SaxxText>
        </div>

        <div class="col-md-3">
          <SaxxText name="postal" :form="formName" required>{{ language.label_postal_code }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText name="website" :form="formName">{{
              language.text_approved_website_address_for_online_selling
            }}
          </SaxxText>
        </div>
      </div>

      <div v-if="requiresResaleCert" class="row">
        <div class="col-md-6">
          <SaxxText
              name="resaleCertNumber"
              :form="formName"
              required
          >{{ language.label_resale_certificate_number }}
          </SaxxText>
        </div>

        <div class="col-md-6">
          <slot name="certImage">
            <SaxxUpload name="certImage" :form="formName" required>{{
                language.label_resale_certificate_upload
              }}
            </SaxxUpload>
          </slot>
        </div>
      </div>
      <div v-else-if="fields.country != 'USA'" class="row">
        <div class="col-md-6">
          <div v-if="showProvincialTaxExemptAlert" class="alert alert-info">
            <p>{{ language.text_pst_number_required_for_bc_mb_sk }}</p>
          </div>
          <SaxxText name="salesTaxVat" :form="formName" :required="requiresSalesTaxVAT">{{
              language.label_sales_tax_vat
            }}
          </SaxxText>
        </div>
        <div class="col-md-6" v-if="fields.country == 'FR'">
          <SaxxText name="resaleCertNumber" :form="formName" :required="true">{{
              language.label_siren_number
            }}
          </SaxxText>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4">
          <SaxxSelect
              name="companyType"
              :items="companyTypes"
              :form="formName"
              required
          >{{ language.label_company_type }}
          </SaxxSelect>
        </div>
        <div class="col-md-4">
          <SaxxSelect
              name="storeType"
              :items="stores"
              :form="formName"
              required
          >{{ language.label_store_type }}
          </SaxxSelect>
        </div>
        <div class="col-md-4">
          <SaxxText
              name="numberStore"
              pattern="\d*"
              :form="formName"
              :validator="v => Number(v) >= 0"
              required
          >{{ language.label_number_of_stores }}
          </SaxxText>
        </div>
      </div>
    </section>

    <section class="card__body">
      <div class="row">
        <div class="col-xs-12">
          <h4>
            Please note that we DO NOT ship to PO Boxes. Please provide a valid Ship To address with your application.
            Thank you.
          </h4>
        </div>
      </div>

      <h3>{{ language.title_shipping_address }}</h3>

      <div class="row">
        <div class="col-md-12">
          <SaxxCheckbox
              name="differentShippingAddress"
              :form="formName"
          >{{ language.label_different_shipping_address }}
          </SaxxCheckbox>
        </div>
      </div>

      <template v-if="fields.differentShippingAddress">
        <div class="row">
          <div class="col-md-6">
            <SaxxSelect
                name="shipping_country"
                :items="countries"
                :form="formName"
                required
            >{{ language.label_shipping_country }}
            </SaxxSelect>
          </div>

          <div class="col-md-6">
            <SaxxSelect
                v-if="shipping_provinces.length > 0"
                name="shipping_province"
                :items="shipping_provinces"
                :form="formName"
                required
            >
              <template v-if="fields.shipping_country === 'USA'">
                {{ language.label_shipping_state }}
              </template>
              <template v-else>
                {{ language.label_shipping_province }}
              </template>
            </SaxxSelect>
            <SaxxText v-else name="shipping_province" :form="formName">{{ language.label_shipping_province }}</SaxxText>
          </div>

          <div class="col-md-6"></div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <SaxxText name="shippingAddress" maxlength="35" :form="formName" required>{{ language.label_shipping_line_1 }}</SaxxText>
          </div>

          <div class="col-md-6">
            <SaxxText name="shippingAddress2" maxlength="20" :form="formName">{{ language.label_shipping_line_2 }}</SaxxText>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <SaxxText name="shippingCity" :form="formName" required>{{ language.label_city }}</SaxxText>
          </div>

          <div class="col-md-6">
            <SaxxText name="shippingPostal" :form="formName" required>{{ language.label_postal_code }}</SaxxText>
          </div>
        </div>

        <div class="row">
          <div class="col-md-2 col-md-offset-10">
            <el-button @click="addShippingAddress" type="success" icon="el-icon-plus" circle></el-button>
          </div>
        </div>
        <div v-for="(address, index) in fields.additional_addresses" class="m-t-2">
          <h4 class="m-b-2">Address {{ index + 2 }}</h4>
          <div class="row">
            <div class="col-md-6">
              <SaxxSelect
                  :name="`additional_addresses[${index}].shipping_country`"
                  :items="countries"
                  :form="formName"
                  :change="(value) => handleCountryChange(index, value)"
              >{{ language.label_shipping_country }}
              </SaxxSelect>
            </div>

            <div class="col-md-6">
              <SaxxSelect
                  v-if="additional_shipping_provinces[index] && additional_shipping_provinces[index].length > 0"
                  :name="`additional_addresses[${index}].shipping_province`"
                  :items="additional_shipping_provinces[index]"
                  :form="formName"
              >
                <template v-if="fields.additional_addresses[index].shipping_country === 'USA'">
                  {{ language.label_shipping_state }}
                </template>
                <template v-else>
                  {{ language.label_shipping_province }}
                </template>
              </SaxxSelect>
              <SaxxText v-else :name="`additional_addresses[${index}][shipping_province]`" :form="formName">
                {{ language.label_shipping_province }}
              </SaxxText>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <SaxxText :name="`additional_addresses[${index}][shippingAddress]`" maxlength="35" :form="formName">
                {{ language.label_shipping_line_1 }}
              </SaxxText>
            </div>

            <div class="col-md-6">
              <SaxxText :name="`additional_addresses[${index}][shippingAddress2]`" maxlength="20" :form="formName">
                {{ language.label_shipping_line_2 }}
              </SaxxText>
            </div>
          </div>

          <div class="row">
            <div class="col-md-6">
              <SaxxText :name="`additional_addresses[${index}][shippingCity]`" :form="formName">{{
                  language.label_city
                }}
              </SaxxText>
            </div>

            <div class="col-md-6">
              <SaxxText :name="`additional_addresses[${index}][shippingPostal]`" :form="formName">
                {{ language.label_postal_code }}
              </SaxxText>
            </div>
          </div>
        </div>
      </template>
    </section>

    <section class="card__body">
      <h3>{{ language.title_contact }}</h3>
      <div class="row m-l-2">
        <div class="col-md-12"><h4>{{ language.title_owner }}</h4></div>
        <div class="col-md-4">
          <SaxxText
              name="customerEmail"
              type="email"
              :form="formName"
              required
          >{{ language.label_customer_email }}
          </SaxxText>
        </div>
        <div class="col-md-4">
          <SaxxText name="ownerName" :form="formName" required>{{ language.label_name_of_owner }}</SaxxText>
        </div>

        <div class="col-md-4">
          <SaxxText name="ownerPhone" :form="formName" required>{{ language.label_phone }}</SaxxText>
        </div>
      </div>

      <div class="row m-l-2">
        <div class="col-md-6">
          <SaxxText
              name="businessEmail"
              type="email"
              :form="formName"
              required
          >{{ language.label_business_email }}
          </SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText name="businessPhone" :form="formName" required>{{ language.label_business_phone }}</SaxxText>
        </div>
      </div>

      <div class="row m-l-2">
        <div class="col-md-12"><h4>{{ language.title_buyer }}</h4></div>
        <div class="col-md-12">
          <SaxxCheckbox name="sameInfoBuyer" :form="formName">{{ language.label_same_buyer_details }}</SaxxCheckbox>
        </div>
      </div>
      <div v-if="!fields.sameInfoBuyer" class="row m-l-2">
        <div class="col-md-4">
          <SaxxText name="buyerName" :form="formName" required>{{ language.label_buyer_name }}</SaxxText>
        </div>
        <div class="col-md-4">
          <SaxxText name="buyerEmail" type="email" :form="formName" required>{{ language.label_buyer_email }}</SaxxText>
        </div>
        <div class="col-md-4">
          <SaxxText name="buyerPhone" :form="formName" required>{{ language.label_buyer_phone }}</SaxxText>
        </div>
      </div>

      <div class="row m-l-2">
        <div class="col-md-12"><h4>{{ language.title_billing_contact }}</h4></div>
        <div class="col-md-12">
          <SaxxCheckbox
              name="sameInfoForRep"
              :form="formName"
          >{{ language.label_same_billing_contact_details }}
          </SaxxCheckbox>
        </div>
      </div>
      <div v-if="!fields.sameInfoForRep" class="row m-l-2">
        <div class="col-md-4">
          <SaxxText name="billing_rep_name" :form="formName" required>{{ language.label_billing_name }}</SaxxText>
        </div>
        <div class="col-md-4">
          <SaxxText name="billing_rep_email" type="email" :form="formName" required>{{
              language.label_billing_email
            }}
          </SaxxText>
        </div>
        <div class="col-md-4">
          <SaxxText name="billing_rep_phone" :form="formName" required>{{ language.label_phone }}</SaxxText>
        </div>
      </div>
    </section>

    <section class="card__body">
      <h3>{{ language.title_business }}</h3>

      <div class="row">
        <div class="col-md-6 agency">
          <SaxxSelect name="agency" :items="agencies" :form="formName" required>{{ language.label_agency }}</SaxxSelect>
        </div>

        <div class="col-md-6">
          <SaxxSelect
              name="channel"
              :items="channels"
              :form="formName"
              required
          >{{ language.label_sales_channel }}
          </SaxxSelect>
        </div>
      </div>
    </section>

    <section class="card__body">
      <h3>{{ language.title_sales_rep }}</h3>

      <div class="row">
        <div class="col-md-6">
          <SaxxText name="repName" :form="formName" required>{{ language.label_sales_rep_name }}</SaxxText>
        </div>
        <div class="col-md-6">
          <SaxxText name="repEmail" type="email" :form="formName" required>{{
              language.label_sales_rep_email
            }}
          </SaxxText>
        </div>
      </div>
    </section>

    <section class="card__body">
      <h3>{{ language.title_background }}</h3>
      <div class="row">
        <div class="col-md-12">
          <SaxxCheckbox name="everBankruptcy" :form="formName">
            {{ language.label_have_filed_for_bankruptcy }}
          </SaxxCheckbox>
        </div>
      </div>

      <div v-if="fields.everBankruptcy" class="row">
        <div class="col-md-6">
          <SaxxDatePicker
              name="bankruptReason"
              :form="formName"
              type="month"
              value-format="dd/MM/yyyy"
              format="MM/yyyy"
              required
          >{{ language.label_when_was_bankruptcy_filed }}
          </SaxxDatePicker>
        </div>
      </div>
    </section>

    <section class="card__body">
      <h3>{{ language.title_payment }}</h3>

      <div class="row">
        <div class="col-md-6">
          <SaxxRadio label="bank" name="paymentTerms" :form="formName" required>{{
              language.option_terms_with_saxx
            }}
          </SaxxRadio>
          <SaxxRadio label="prepayment" name="paymentTerms" :form="formName" required>{{
              language.option_prepayment
            }}
          </SaxxRadio>
        </div>
      </div>
    </section>

    <template v-if="fields.paymentTerms === 'bank'">
      <section class="card__body">
        <h3>{{ language.title_payment_term_bank }}</h3>

        <div class="row">
          <div class="col-md-4">
            <SaxxText name="bankName" :form="formName" required>{{ language.label_payment_bank_name }}</SaxxText>
          </div>

          <div class="col-md-4">
            <SaxxText name="bankContactName" :form="formName" required>{{
                language.label_payment_bank_contact_name
              }}
            </SaxxText>
          </div>

          <div class="col-md-4">
            <SaxxText name="bankContactPhone" :form="formName" required>{{
                language.label_payment_bank_contact_phone
              }}
            </SaxxText>
          </div>
        </div>
      </section>

      <section class="card__body" v-if="fields.country != 'FR'">
        <h3>{{ language.title_supplier }}</h3>

        <div class="row">
          <div class="col-md-6">
            <SaxxText name="supplierName" :form="formName" required>{{ language.label_supplier_name_1 }}</SaxxText>
          </div>
          <div class="col-md-6">
            <SaxxText name="supplierContactName" :form="formName" required>{{
                language.label_supplier_contact_name_1
              }}
            </SaxxText>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <SaxxText
                name="supplierEmail"
                type="email"
                :form="formName"
                required
            >{{ language.label_supplier_email_1 }}
            </SaxxText>
          </div>
          <div class="col-md-6">
            <SaxxText name="supplierPhone" :form="formName" required>{{ language.label_supplier_phone_1 }}</SaxxText>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <SaxxText name="supplierName2" :form="formName" required>{{ language.label_supplier_name_2 }}</SaxxText>
          </div>
          <div class="col-md-6">
            <SaxxText name="supplierContactName2" :form="formName" required>{{
                language.label_supplier_contact_name_2
              }}
            </SaxxText>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <SaxxText
                name="supplierEmail2"
                type="email"
                :form="formName"
                required
            >{{ language.label_supplier_email_2 }}
            </SaxxText>
          </div>
          <div class="col-md-6">
            <SaxxText name="supplierPhone2" :form="formName" required>{{ language.label_supplier_phone_2 }}</SaxxText>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <SaxxText name="supplierName3" :form="formName" required>{{ language.label_supplier_name_3 }}</SaxxText>
          </div>
          <div class="col-md-6">
            <SaxxText name="supplierContactName3" :form="formName" required>{{
                language.label_supplier_contact_name_3
              }}
            </SaxxText>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <SaxxText
                name="supplierEmail3"
                type="email"
                :form="formName"
                required
            >{{ language.label_supplier_email_3 }}
            </SaxxText>
          </div>
          <div class="col-md-6">
            <SaxxText name="supplierPhone3" :form="formName" required>{{ language.label_supplier_phone_3 }}</SaxxText>
          </div>
        </div>
      </section>
    </template>

    <section v-if="fields.paymentTerms === 'prepayment'" class="card__body">
      <p>
        {{ language.text_prepayment_information }}
      </p>
      <p>
        {{ language.text_prepayment_note }}
      </p>
    </section>

    <section class="card__body">
      <h3>{{ language.title_notifications }}</h3>
      <p>
        {{ language.text_notifications_information }}
      </p>

      <div class="row">
        <div class="col-md-12">
          <SaxxCheckbox name="sameEmailForConfirmation" :form="formName">
            {{ language.label_use_same_email_for_confirmation_and_invoices }}
          </SaxxCheckbox>
        </div>
      </div>
      <div v-if="fields.sameEmailForConfirmation" class="row">
        <div class="col-md-6">
          <SaxxText
              name="invoiceEmail"
              type="email"
              :form="formName"
              required
          >{{ language.label_notification_emails }}
          </SaxxText>
        </div>
      </div>
      <div v-if="!fields.sameEmailForConfirmation" class="row">
        <div class="col-md-6">
          <SaxxText name="invoiceEmail" type="email" :form="formName" required>{{
              language.label_invoice_emails
            }}
          </SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText
              name="confirmationEmailAddress"
              type="email"
              :form="formName"
              required
          >{{ language.label_order_confirmation_emails }}
          </SaxxText>
        </div>
      </div>
    </section>

    <section class="card__body">
      <div class="row">
        <div class="col-md-12">
          <h2>{{ language.title_terms_and_conditions }}</h2>
          <template v-if="fields.country === 'USA'">
            <ol>
              <li v-for="(item, index) in language.list_terms_and_conditions_usa" :key="index">{{ item }}</li>
            </ol>

            <h3>{{ language.title_vendor_and_map_policies }}</h3>
            <p>
              {{ language.text_vendor_and_map_policies }}
            </p>
            <ul>
              <li>
                <a
                    href="/documents/b2b/2026/us/SAXX_Vendor_Policy_USA.pdf"
                    target="_blank"
                >{{ language.label_vendor_policy }}</a>
              </li>
              <li>
                <a
                    href="/documents/b2b/2026/us/SAXX_MAP_Policy_USA.pdf"
                    target="_blank"
                >{{ language.label_map_policy }}</a>
              </li>
            </ul>
          </template>
          <template v-else>
            <ol>
              <li v-for="(item, index) in language.list_terms_and_conditions_canada_and_international" :key="index">
                {{ replaceCurrency(item) }}
              </li>
            </ol>
          </template>
          <template v-if="fields.country === 'CA'">
            <h3>{{ language.title_vendor_and_map_policies }}</h3>
            <p>
              {{ language.text_vendor_and_map_policies }}
            </p>
            <ul>
              <li>
                <a
                    href="/documents/b2b/2026/ca/SAXX_Vendor_Policy_CAN.pdf"
                    target="_blank"
                >{{ language.label_vendor_policy }}</a>
              </li>
              <li>
                <a
                    href="/documents/b2b/2026/ca/SAXX_MAP_Policy_CAN.pdf"
                    target="_blank"
                >{{ language.label_map_policy }}</a>
              </li>
            </ul>
          </template>
          <template v-if="eu_countries.includes(fields.country)">
            <h3>{{ language.label_vendor_policy }}</h3>
            <p>
              {{ language.text_vendor_and_map_policies }}
            </p>
            <ul>
              <li>
                <a
                    href="/documents/b2b/2020/eu/EU_Vendor_Policy_EN.pdf"
                    target="_blank"
                >
                  {{ language.label_vendor_policy }} - In English
                </a>
              </li>
              <li>
                <a
                    href="/documents/b2b/2020/eu/EU_Vendor_Policy_FR.pdf"
                    target="_blank"
                >
                  {{ language.label_vendor_policy }} - In French - POLITIQUE DES DÉTAILLANTS EN EUROPE
                </a>
              </li>
              <li>
                <a
                    href="/documents/b2b/2020/eu/EU_Vendor_Policy_DE.pdf"
                    target="_blank"
                >
                  {{ language.label_vendor_policy }} - In German - EINZELHÄNDLER RICHTLINIE
                </a>
              </li>
            </ul>
          </template>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <h2>{{ language.title_enter_name_and_position }}</h2>
          <small>
            {{ language.text_enter_name_and_position_politely }}
          </small>
        </div>
      </div>

      <div class="row">
        <div class="col-md-6">
          <SaxxText name="applicantName" :form="formName" required>{{ language.label_legal_name }}</SaxxText>
        </div>

        <div class="col-md-6">
          <SaxxText name="applicantPosition" :form="formName" required>{{ language.label_legal_position }}</SaxxText>
        </div>
      </div>

      <div class="row">
        <div class="col-md-12">
          <SaxxCheckbox name="agreeToTermsAndConditions" :form="formName" required>
            {{ language.label_legal_i_agree_to_terms_conditions_policies }}
          </SaxxCheckbox>
        </div>
      </div>
    </section>

    <section class="card__body">
      <slot>
        <div class="row">
          <div class="col-md-12">
            <button
                class="btn btn-saxx"
                type="submit"
                :disabled="isLoading || !fields.agreeToTermsAndConditions"
            >{{ language.label_submit }}
            </button>
          </div>
        </div>
      </slot>
    </section>
  </form>
</template>

<script>
import {ErrorlessRepository as axios} from '../../repositories/Repository';
import {mapActions, mapGetters} from 'vuex';
import {language} from '../../lang';

import SaxxCheckbox from '../forms/SaxxCheckbox';
import SaxxDatePicker from '../forms/SaxxDatePicker';
import SaxxSelect from '../forms/SaxxSelect';
import SaxxText from '../forms/SaxxText';
import SaxxRadio from '../forms/SaxxRadio';
import SaxxUpload from '../forms/SaxxUpload';

export default {
  name: 'ApplicationForm',
  components: {
    SaxxCheckbox,
    SaxxDatePicker,
    SaxxSelect,
    SaxxText,
    SaxxRadio,
    SaxxUpload,
  },
  props: {
    formName: {
      required: true,
      type: String,
    },
    lang: {
      required: true,
      type: String,
      default: 'en',
    },
    additionalFields: {
      required: false,
      type: Object,
      default: () => {},
    },
  },
  created() {
    this.createForm({
      name: this.formName,
      fields: {
        ...this.additionalFields,

        companyName: '',
        tradeName: '',
        businessEmail: '',
        businessPhone: '',

        salesTaxVat: '',
        resaleCertNumber: '',
        certImage: null,

        country: '',
        province: '',
        city: '',
        address: '',
        address2: '',
        postal: '',
        website: '',

        agency: '',
        channel: '',

        differentShippingAddress: false,
        shipping_country: '',
        shipping_province: '',
        shippingCity: '',
        shippingAddress: '',
        shippingAddress2: '',
        shippingPostal: '',
        additional_addresses: [],

        ownerName: '',
        ownerPhone: '',
        customerEmail: '',

        sameInfoBuyer: true,
        buyerName: '',
        buyerEmail: '',
        buyerPhone: '',

        sameInfoForRep: true,
        repName: '',
        repEmail: '',

        billing_rep_name: '',
        billing_rep_email: '',
        billing_rep_phone: '',

        companyType: '',
        yearOwned: '',
        leaseExpires: '',
        storeType: '',
        numberStore: '',
        monthlyMortgage: '',
        rentOrOwn: '',

        everBankruptcy: false,
        bankruptReason: '',

        sameEmailForConfirmation: true,
        invoiceEmail: '',
        confirmationEmailAddress: '',

        paymentTerms: '',
        bankName: '',
        bankContactName: '',
        bankContactPhone: '',
        supplierName: '',
        supplierEmail: '',
        supplierPhone: '',
        supplierContactName: '',
        supplierName2: '',
        supplierEmail2: '',
        supplierPhone2: '',
        supplierContactName2: '',
        supplierName3: '',
        supplierEmail3: '',
        supplierPhone3: '',
        supplierContactName3: '',

        applicantName: '',
        applicantPosition: '',

        agreeToTermsAndConditions: false,
        language: this.lang,
      },
    });

    this.fetchCountries();
    this.fetchChannels();
  },
  data() {
    return {
      companyTypes: [
        {
          label: 'Proprietor',
          value: 'proprietor',
        },
        {
          label: 'Partnership',
          value: 'partnership',
        },
        {
          label: 'Limited Company',
          value: 'limited',
        },
        {
          label: 'Incorporated',
          value: 'incorporated',
        },
      ],
      rentTypes: [
        {
          label: 'Rent',
          value: 'rent',
        },
        {
          label: 'Own',
          value: 'own',
        },
      ],
      countries: [],
      eu_countries: [
        'GB', 'IE', 'IM', 'GK', 'FR', 'GP', 'MQ', 'GF', 'TY', 'NC', 'WF', 'RE', 'PF', 'PM', 'MU', 'SC',
        'MC', 'DE', 'AT', 'BE', 'LU', 'LI', 'NL', 'SX', 'CW', 'AW',
      ],
      shipping_provinces: [],
      additional_shipping_provinces: {},
      provinces: [],
      stores: [],
      billingAgencies: [],
      shippingAgencies: [],
      channels: [],
    };
  },
  methods: {
    ...mapActions('forms', [
      'createForm',
      'setField',
      'setErrors',
    ]),
    async submit() {
      if (!this.$refs.formElement.checkValidity()) {
        return this.$refs.formElement.reportValidity();
      }

      this.setErrors({
        form: this.formName,
        errors: [],
      });

      this.$emit('submit', {...this.fields});
    },
    async fetchCountries() {
      const response = await axios.get('/credit-dashboard/metadata/countries');

      this.countries = response.data;
    },
    async fetchChannels(currentChannel = null) {
      const params = {
        context: this.isReviewForm ? 'review' : 'new',
      };

      if (this.isReviewForm && currentChannel) {
        params.current = currentChannel;
      }

      const response = await axios.get('/credit-dashboard/metadata/channels', {params});

      this.channels = response.data;
    },
    async fetchProvinces(country) {
      this.provinces = [];
      const response = await axios.get(
          `/credit-dashboard/metadata/countries/${country}/provinces`,
      );

      this.provinces = response.data;
    },
    async fetchShippingProvinces(country) {
      this.shipping_provinces = [];
      const response = await axios.get(
          `/credit-dashboard/metadata/countries/${country}/provinces`,
      );
      this.shipping_provinces = response.data;
    },
    async fetchAdditionalShippingProvinces(country, index) {
      this.additional_shipping_provinces[index] = [];
      const response = await axios.get(
          `/credit-dashboard/metadata/countries/${country}/provinces`,
      );
      this.additional_shipping_provinces[index] = response.data;
    },
    async fetchAgencies(country) {
      this.billingAgencies = [];
      const response = await axios.get(
          `/credit-dashboard/metadata/countries/${country}/agencies`,
      );

      this.billingAgencies = response.data;
    },
    async fetchShippingAgencies(country) {
      this.shippingAgencies = [];
      const response = await axios.get(
          `/credit-dashboard/metadata/countries/${country}/agencies`,
      );

      this.shippingAgencies = response.data;
    },
    async fetchStoreTypes(country) {
      this.stores = [];
      const response = await axios.get(
          `/credit-dashboard/metadata/countries/${country}/store-types`,
      );

      this.stores = response.data;
    },
    replaceCurrency(text) {
      if (this.fields.country === 'GB') {
        return text.replace('$', '£');
      } else if (this.fields.country === 'CA') {
        return text;
      } else {
        return text.replace('$', '€');
      }
    },
    addShippingAddress() {
      const addresses = _.cloneDeep(this.fields.additional_addresses) || [];
      addresses.push({
        shipping_country: '',
        shipping_province: '',
        shippingCity: '',
        shippingAddress: '',
        shippingAddress2: '',
        shippingPostal: '',
      });
      this.setField({
        form: this.formName,
        field: 'additional_addresses',
        value: addresses,
      });
    },
    async handleCountryChange(index, value) {
      await this.fetchAdditionalShippingProvinces(value, index);
      this.$forceUpdate();
    },
  },
  computed: {
    ...mapGetters('forms', ['getFields', 'getStatus']),
    fields() {
      return this.getFields({
        form: this.formName,
      });
    },
    monthlyLandCostType() {
      switch (this.fields.rentOrOwn) {
        case 'rent':
          return 'Rent';
        case 'own':
          return 'Mortgage';
        default:
          return 'Cost';
      }
    },
    requiresResaleCert() {
      return this.fields.country === 'USA' && !_.includes(['AK', 'NH', 'OR'], this.fields.province);
    },
    requiresSalesTaxVAT() {
      if (this.fields.country === 'USA') {
        return false;
      }

      if (this.fields.country === 'CA') {
        return this.showProvincialTaxExemptAlert;
      }

      return true;
    },
    isLoading() {
      return this.getStatus({form: this.formName}) === 'loading';
    },
    agencies() {
      if (!this.fields.differentShippingAddress) {
        return this.billingAgencies;
      }

      return this.billingAgencies.concat(this.shippingAgencies);
    },
    showProvincialTaxExemptAlert() {
      const taxExemptProvinces = ['BC', 'MB', 'SK'];
      if (!this.fields.differentShippingAddress) {
        return (
            this.fields.country === 'CA' &&
            taxExemptProvinces.includes(this.fields.province)
        );
      }

      return (
          this.fields.shipping_country === 'CA' &&
          taxExemptProvinces.includes(this.fields.shipping_province)
      );
    },
    language() {
      const lang = language(this.lang);

      return lang.creditDashboard;
    },
    isReviewForm() {
      return this.formName.includes('.review');
    },
  },
  watch: {
    'fields.country': function(country) {
      if (!String(country).trim().length) {
        return;
      }

      this.fetchProvinces(country);
      this.fetchAgencies(country);
      this.fetchStoreTypes(country);
    },
    'fields.shipping_country': function(country) {
      if (!country || !country.trim()) {
        return;
      }
      this.fetchShippingAgencies(country);
      this.fetchShippingProvinces(country);
    },
    'fields.businessEmail': function(to, from) {
      if (!this.fields.sameInfoForRep) {
        return;
      }

      if (this.fields.invoiceEmail === from) {
        this.setField({
          form: this.formName,
          field: 'invoiceEmail',
          value: to,
        });
      }
    },
    'fields.billing_rep_email': function(to, from) {
      if (this.fields.sameInfoForRep) {
        return;
      }

      if (this.fields.invoiceEmail === from) {
        this.setField({
          form: this.formName,
          field: 'invoiceEmail',
          value: to,
        });
      }
    },
    'fields.invoiceEmail': function(to, from) {
      if (this.fields.sameEmailForConfirmation) {
        this.setField({
          form: this.formName,
          field: 'confirmationEmailAddress',
          value: to,
        });
      }
    },
    'fields.channel': function(channel) {
      if (!this.isReviewForm || !String(channel || '').trim().length) {
        return;
      }

      this.fetchChannels(channel);
    },
  },
};
</script>
